/**
 * PostHog analytics helper — initializes PostHog and provides typed event tracking.
 *
 * Required env vars (safe no-op when missing):
 *   VITE_POSTHOG_KEY   – PostHog project API key
 *   VITE_POSTHOG_HOST  – PostHog instance host (e.g. https://us.i.posthog.com)
 *
 * Features enabled:
 *   - Session recording (form inputs masked)
 *   - Exception / error autocapture
 *   - Page view + page leave tracking
 *   - Rich user identification (plan, referral source, demographics)
 */

import posthog from 'posthog-js';

/**
 * Returns true when the current host is a local dev environment.
 * Used to suppress PostHog so localhost test purchases / signups don't
 * pollute prod analytics — set VITE_POSTHOG_LOCAL=true to override.
 */
function isLocalDev(): boolean {
  if (typeof window === 'undefined') return false;
  if (import.meta.env.VITE_POSTHOG_LOCAL === 'true') return false;
  const host = window.location.hostname;
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '0.0.0.0' ||
    host.endsWith('.local') ||
    /^192\.168\./.test(host) ||
    /^10\./.test(host)
  );
}

// Force PostHog into no-op mode on localhost by treating the key as unset.
// Every helper below already short-circuits when POSTHOG_KEY is falsy, so
// this single gate suppresses init, identify, alias, and every capture
// without having to repeat the check at each call site.
const POSTHOG_KEY = isLocalDev()
  ? undefined
  : (import.meta.env.VITE_POSTHOG_KEY as string | undefined);
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST as string | undefined;

/* -------------------------------------------------- */
/*  Initialization
/* -------------------------------------------------- */

/**
 * Initialize PostHog. Call once at app startup (e.g. in main.tsx).
 * No-op if VITE_POSTHOG_KEY is not set, or if running on localhost
 * (override with VITE_POSTHOG_LOCAL=true).
 */
export function initPostHog(bootstrapDistinctId?: string): void {
  if (!POSTHOG_KEY || !POSTHOG_HOST) return;

  // In production, proxy through Vercel (/ingest → us.i.posthog.com) so that
  // ad blockers can't block PostHog. In local dev, talk to PostHog directly —
  // PostHog accepts CORS from localhost and there's no need to fight with Vite's
  // http-proxy SSL quirks just to track dev events.
  const apiHost = import.meta.env.DEV ? POSTHOG_HOST : '/ingest';

  posthog.init(POSTHOG_KEY, {
    api_host: apiHost,
    ui_host: POSTHOG_HOST, // keeps toolbar pointing at posthog.com
    defaults: '2026-01-30',
    // ⚠️ 2026-08-08: this was `true`, which in posthog-js means "capture a
    // pageview on full page load ONLY". The history-autocapture integration is
    // gated on `capture_pageview === 'history_change'`, so every React Router
    // navigation inside this SPA produced no $pageview at all. Measured: 1.21
    // pageviews per session across 98 sessions on a 19-route app. Every
    // page-based funnel, path analysis and drop-off report for the
    // authenticated product was silently wrong.
    capture_pageview: 'history_change',
    capture_pageleave: true,
    persistence: 'localStorage+cookie',

    // Session recordings — mask all form inputs except email so we can watch
    // checkout and bill submission flows without exposing PII.
    session_recording: {
      maskAllInputs: true,
      maskInputOptions: {
        password: true,
        email: false,      // emails are fine to show in recordings
        text: false,       // show text fields (provider names, etc.)
        tel: false,
      },
    },

    // Cross-domain identity stitching. The marketing site (mediloop.ai) hands
    // off its PostHog distinct_id via the checkout success URL (ph_distinct_id).
    // Booting this app session as that SAME person means the post-checkout
    // `purchase` event lands on the distinct_id that did the pre-checkout
    // browsing (lp_viewed → begin_checkout), so the funnel links end-to-end.
    // posthog.alias() couldn't reliably merge two already-active anonymous
    // people; bootstrap avoids the merge entirely. Returning app users arrive
    // with no param and keep their persisted id.
    ...(bootstrapDistinctId ? { bootstrap: { distinctID: bootstrapDistinctId } } : {}),

    // Auto-capture uncaught JS errors and unhandled promise rejections.
    capture_exceptions: true,
  });
}

/* -------------------------------------------------- */
/*  Error tracking (belt-and-suspenders)
/* -------------------------------------------------- */

/**
 * Register window-level error handlers to ensure all uncaught errors and
 * unhandled promise rejections are sent to PostHog, even if PostHog's own
 * autocapture doesn't fire (e.g. in sandboxed iframes).
 * Call once after initPostHog().
 */
export function setupErrorTracking(): void {
  // ⚠️ 2026-08-08: intentionally a no-op.
  //
  // This used to register its own `error` / `unhandledrejection` listeners and
  // capture `$exception` manually. But `capture_exceptions: true` in init()
  // already makes posthog-js wrap window.onerror and onunhandledrejection, so
  // every uncaught error was captured TWICE — once properly (with
  // `$exception_list`, which Error Tracking groups on) and once with legacy
  // flat props, which landed as a separate, badly-grouped issue alongside the
  // real one. Sentry (initSentry in main.tsx) is a third capture path and is
  // left alone.
  //
  // Kept as an exported no-op so main.tsx and any other caller keep compiling.
  return;
}

/* -------------------------------------------------- */
/*  User identification
/* -------------------------------------------------- */

type UserProperties = {
  // Test accounts (profiles.is_test). The project's test filter drops
  // anyone with this set, so their clicks stay out of the funnels.
  $internal_or_test_user?: boolean;
  email?: string;
  name?: string;
  plan?: string;
  subscription_status?: string;
  bills_used?: number;
  bills_limit?: number;
  referral_source?: string;
  gender?: string;
  state?: string;
  created_at?: string;
  // First-touch marketing attribution — written to profiles at onboarding,
  // sent here so PostHog person-level filters work without having to look
  // up Supabase. utm_* are captured from the URL on Signup.tsx; the
  // self-reported referral_source above is the onboarding survey answer.
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  initial_referrer?: string;
  landing_page?: string;
  acquisition_channel?: string;
};

/**
 * Identify the current user after authentication.
 * Links all previous anonymous events to this user and sets person properties
 * so funnels can be segmented by plan, referral source, geography, etc.
 */
export function identifyUser(userId: string, properties: UserProperties = {}): void {
  if (!POSTHOG_KEY) return;

  // Strip undefined/null/empty values so PostHog doesn't overwrite existing props
  const props = Object.fromEntries(
    Object.entries(properties).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );

  posthog.identify(userId, props);
}

/**
 * Point an additional distinct_id at the CURRENT person.
 *
 * posthog.alias(x) tells PostHog "x is the same human as whoever I am right
 * now". We use it in two places, both of which were leaving orphaned people
 * behind before 2026-08-08:
 *
 *   1. main.tsx — the marketing site's distinct_id arrives as ?ph_distinct_id.
 *      `bootstrap.distinctID` only takes effect when posthog-js has NO
 *      persisted id, so it silently did nothing for every returning app user.
 *      Aliasing works regardless of whether an id is already persisted.
 *
 *   2. PostHogIdentifier — apps/server keys its events on the user's EMAIL
 *      (posthogCapture.ts, checkoutLink.ts, resendWebhook.ts) while this app
 *      identifies on the Supabase user UUID. Nothing merged them: all 559
 *      email_delivered/opened/clicked events in 90 days sat on email-keyed
 *      people with ZERO overlap with product events, so email_clicked →
 *      purchase was unbuildable. Aliasing the email onto the UUID person
 *      merges the two.
 *
 * Alias is permanent and one-way in PostHog, so we guard against re-sending
 * the same alias: it is wasted volume and makes the activity log unreadable.
 */
const ALIAS_GUARD_PREFIX = 'ph_aliased:';

export function aliasIdentity(alias: string | null | undefined): void {
  if (!POSTHOG_KEY || !alias) return;

  const value = String(alias).trim();
  if (!value) return;

  try {
    // Never alias an id to itself — PostHog rejects it and it means the
    // identity already matches, which is the outcome we wanted anyway.
    if (posthog.get_distinct_id?.() === value) return;
  } catch {
    /* SDK not ready enough to compare — the guard below still protects us */
  }

  const guardKey = `${ALIAS_GUARD_PREFIX}${value}`;
  try {
    if (localStorage.getItem(guardKey)) return;
  } catch {
    /* localStorage unavailable — proceed, a duplicate alias is harmless */
  }

  posthog.alias(value);

  try {
    localStorage.setItem(guardKey, '1');
  } catch {
    /* best effort */
  }
}

/**
 * @deprecated Use aliasIdentity(). Kept as a thin wrapper so existing call
 * sites keep working.
 */
export function aliasLandingId(landingDistinctId: string): void {
  aliasIdentity(landingDistinctId);
}

/**
 * Reset PostHog identity on logout.
 * Creates a new anonymous ID for future events.
 */
export function resetUser(): void {
  if (!POSTHOG_KEY) return;

  posthog.reset();
}

/* -------------------------------------------------- */
/*  Event tracking
/* -------------------------------------------------- */

/**
 * Fire-once guard for events that must never double-count (revenue, above
 * all). Keyed in localStorage rather than sessionStorage so a page refresh
 * OR a fresh tab still counts as the same fire.
 *
 * ⚠️ 2026-08-08: added because `purchase` was double-counting badly. 90-day
 * check: 26 PostHog `purchase` events across 18 people vs 11 succeeded Stripe
 * charges. Five people fired 2-4 times on the same day — one fired 4x, so a
 * single $69 EOB purchase logged $276 of $revenue. Cause: Signup.tsx never
 * strips the Stripe success query string (Plan.tsx does, via
 * history.replaceState), so a refresh re-runs the verification effect; the
 * Meta and Reddit legs had sessionStorage guards but phPurchase had none.
 */
function fireOnce(key: string): boolean {
  if (!key) return true;
  const guardKey = `ph_fired:${key}`;
  try {
    if (localStorage.getItem(guardKey)) return false;
    localStorage.setItem(guardKey, '1');
  } catch {
    // localStorage unavailable — allow the fire rather than lose the event.
    return true;
  }
  return true;
}

/**
 * Track a purchase event.
 */
export function phPurchase(params: {
  value: number;
  currency?: string;
  plan: string;
  eventId: string;
}): void {
  if (!POSTHOG_KEY) return;

  // 🧪 Guard (2026-07-02): eventId is the Stripe checkout session id —
  // cs_test_* means Stripe test mode (QA). Keep test purchases out of
  // PostHog revenue analytics (mirrors webhook livemode gate).
  if (params.eventId?.startsWith('cs_test_')) return;

  // 🔒 Guard (2026-08-08): one Stripe session = one purchase, forever.
  if (!fireOnce(`purchase:${params.eventId}`)) return;

  posthog.capture('purchase', {
    value: params.value,
    $revenue: params.value,   // powers PostHog revenue dashboard
    currency: params.currency ?? 'USD',
    plan: params.plan,
    event_id: params.eventId,
  });
}

/**
 * Track bill submitted (uploaded to Supabase).
 *
 * This is mediloop's activation event — the strongest predictor of retention
 * we have. Before 2026-08-08 it carried only `provider` and
 * `for_family_member`, which made it impossible to answer the two questions
 * that actually matter: "is this their FIRST bill?" and "did this bill go on
 * to produce savings?". `billId` gives the join key to outcome; `isFirstBill`
 * gives the activation cohort.
 */
export function phBillSubmitted(params: {
  provider?: string;
  forFamilyMember?: boolean;
  billId?: string;
  isFirstBill?: boolean;
  billOrdinal?: number;
  amount?: number;
  hasEob?: boolean;
  hasDescription?: boolean;
  fileType?: string;
  plan?: string;
}): void {
  if (!POSTHOG_KEY) return;

  // Keep this milestone independent of the medical documents and their contents.
  posthog.capture('bill_submitted', {
    is_first_bill: params.isFirstBill ?? null,
    bill_ordinal: params.billOrdinal ?? null,
    plan: params.plan ?? null,
  });
}

/**
 * Track signup OTP requested.
 */
export function phSignupOtpSent(email: string): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('signup_otp_sent', { email });
}

/**
 * Track login OTP requested.
 */
export function phLoginOtpSent(email: string): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('login_otp_sent', { email });
}

/**
 * Track successful OTP verification.
 *
 * `intent` distinguishes a genuine registration from a returning login —
 * /verify is the shared OTP screen for BOTH Signup and Login, so before
 * 2026-08-08 a single undifferentiated `otp_verified` covered the two and no
 * signup funnel could be built from it.
 */
export function phOtpVerified(email: string, intent: 'signup' | 'login' | 'unknown' = 'unknown'): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('otp_verified', { email, intent });
}

/** Track a completed registration (first-ever verification for this account). */
export function phSignupCompleted(params: { email?: string; method: 'otp' | 'google' }): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('signup_completed', {
    email: params.email ?? null,
    method: params.method,
  });
}

/** Track a completed login (returning user). */
export function phLoginCompleted(params: { email?: string; method: 'otp' | 'google' }): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('login_completed', {
    email: params.email ?? null,
    method: params.method,
  });
}

/**
 * Track an OAuth redirect being started. Google users never touch /verify, so
 * without this they were invisible in the signup funnel entirely.
 */
export function phOauthStarted(params: { provider: 'google'; intent: 'signup' | 'login' }): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('oauth_started', {
    provider: params.provider,
    intent: params.intent,
  });
}

/**
 * Track a checkout start (redirect to Stripe).
 *
 * ⚠️ 2026-08-08 — EVENT RENAMED: `initiate_checkout` → `begin_checkout`.
 *
 * These were always the same user action under two names: apps/web and
 * apps/server fired `begin_checkout`, this app fired `initiate_checkout`. Any
 * checkout funnel silently missed whichever half it didn't name. `begin_checkout`
 * won because 22 of the 26 saved insights referencing checkout already used it —
 * those insights now get MORE complete, since in-product addon and upgrade
 * checkouts finally appear in them.
 *
 * Historical continuity is handled by the PostHog action "Checkout started
 * (any surface)" (id 314845), which ORs both names. Use that action in funnels
 * rather than either raw event and the rename boundary is invisible.
 *
 * `surface: 'app'` preserves the one thing the split was accidentally giving
 * us — the ability to tell in-product expansion checkouts apart from
 * marketing-site acquisition checkouts. Filter on it instead of on the old
 * event name.
 *
 * `type` was originally just 'addon' | 'upgrade', which collapsed
 * eob_to_one_off, eob_to_annual and one_off_to_annual into one
 * indistinguishable bucket even though the Meta leg already passed distinct
 * contentName values for each. `flow` carries that detail.
 *
 * The function keeps its name so call sites don't churn; only the emitted
 * event changed.
 */
export function phInitiateCheckout(
  type: 'addon' | 'upgrade' | 'start_case',
  extra: { flow?: string; value?: number; currency?: string; plan?: string } = {}
): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('begin_checkout', {
    type,
    flow: extra.flow ?? type,
    surface: 'app',
    value: Number.isFinite(Number(extra.value)) ? Number(extra.value) : null,
    currency: extra.currency ?? 'USD',
    plan: extra.plan ?? null,
  });
}

/**
 * Track subscription plan change.
 *
 * `reason` / `comment` are the churn reasons already collected by the cancel
 * modal and written to subscriptions.cancellation_reason (migration 054).
 * They were being sent to the server and then dropped on the floor as far as
 * analytics went, so cancellations could not be cross-tabbed by reason.
 */
export function phPlanChange(
  action: 'upgrade' | 'downgrade' | 'reactivate',
  extra: { reason?: string; comment?: string; plan?: string } = {}
): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('plan_change', {
    action,
    reason: extra.reason ?? null,
    has_comment: Boolean(extra.comment && extra.comment.trim()),
    plan: extra.plan ?? null,
  });
}

/* -------------------------------------------------- */
/*  Product surface (added 2026-08-08)
/* -------------------------------------------------- */

/**
 * Track the savings reveal — the single highest-value untracked event in the
 * product. Until now there was no way to answer "do users who see a positive
 * verdict retain and upgrade more than users who see no_savings", which is
 * the central retention question for this business.
 */
export function phVerdictViewed(params: {
  billId?: string;
  wasTotal?: number;
  savedTotal?: number;
  percentSaved?: number;
  noSavings?: boolean;
  status?: string;
}): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('verdict_viewed', {
    bill_id: params.billId ?? null,
    was_total: Number.isFinite(Number(params.wasTotal)) ? Number(params.wasTotal) : null,
    saved_total: Number.isFinite(Number(params.savedTotal)) ? Number(params.savedTotal) : null,
    percent_saved: Number.isFinite(Number(params.percentSaved))
      ? Number(params.percentSaved)
      : null,
    no_savings: params.noSavings ?? null,
    status: params.status ?? null,
  });
}

/** Track a case / bill detail view. */
export function phCaseViewed(params: { billId?: string; status?: string }): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('case_viewed', {
    bill_id: params.billId ?? null,
    status: params.status ?? null,
  });
}

/** Track a customer downloading a document (letter, statement, attachment). */
export function phDocumentDownloaded(params: {
  billId?: string;
  documentType?: string;
  fileName?: string;
}): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('document_downloaded', {
    bill_id: params.billId ?? null,
    document_type: params.documentType ?? null,
    file_name: params.fileName ?? null,
  });
}

/**
 * Track the customer self-reporting that they paid the settled bill. The
 * closest thing to a "case genuinely resolved" signal we get from their side,
 * and a useful satisfaction proxy against savedTotal.
 */
export function phBillMarkedPaid(params: {
  billId?: string;
  paid: boolean;
  savedTotal?: number;
}): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('bill_marked_paid', {
    bill_id: params.billId ?? null,
    paid: params.paid,
    saved_total: Number.isFinite(Number(params.savedTotal))
      ? Number(params.savedTotal)
      : null,
  });
}

/** Track a customer sending a message on a case. */
export function phCaseMessageSent(params: {
  billId?: string;
  hasAttachment?: boolean;
}): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('case_message_sent', {
    bill_id: params.billId ?? null,
    has_attachment: params.hasAttachment ?? false,
  });
}

/** Track an EOB submission (the $69 product line, previously PostHog-invisible). */
export function phEobSubmitted(params: { fileType?: string; fileCount?: number }): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('eob_submitted', {
    file_type: params.fileType ?? null,
    file_count: params.fileCount ?? null,
  });
}

/** Track an EOB report being opened. */
export function phEobReportViewed(params: { reportId?: string } = {}): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('eob_report_viewed', { report_id: params.reportId ?? null });
}

/** Track a Personalized Action Plan request going in (bill + optional EOB + intake). */
export function phActionPlanSubmitted(params: {
  planId?: string;
  documentCount?: number;
  hasEob?: boolean;
  insurance?: string;
  billStatus?: string;
}): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('action_plan_submitted', {
    plan_id: params.planId ?? null,
    document_count: params.documentCount ?? null,
    has_eob: params.hasEob ?? false,
    insurance: params.insurance ?? null,
    bill_status: params.billStatus ?? null,
  });
}

/** Track a delivered Personalized Action Plan being read. */
export function phActionPlanViewed(params: { planId?: string } = {}): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('action_plan_viewed', { plan_id: params.planId ?? null });
}

/** Track every "Start a new case" click — the resell funnel the closed
 *  case and Home redesign exist to drive. `source` says which surface:
 *  closed_case_next_steps, home_foot, home_empty. */
export function phStartNewCaseClicked(params: { source: string; billId?: string }): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('start_new_case_clicked', {
    source: params.source,
    bill_id: params.billId ?? null,
  });
}

/** Track a referral link being copied or shared. */
export function phReferralShared(params: { method: string; code?: string }): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('referral_shared', {
    method: params.method,
    code: params.code ?? null,
  });
}

/** Track onboarding completion (previously Intercom-only). */
export function phOnboardingCompleted(): void {
  if (!POSTHOG_KEY) return;
  posthog.capture('onboarding_completed', {});
}

/** The in-app feedback card (FeedbackCard.tsx). Score is 1-5, comment
 *  is whatever they typed after tapping a face. The score also lands on
 *  the person so it can be used as a filter later. */
export function phFeedbackSubmitted(params: {
  prompt: string;
  score: number;
  comment: string | null;
}): void {
  if (!POSTHOG_KEY) return;
  posthog.capture('feedback_submitted', {
    prompt: params.prompt,
    score: params.score,
    comment: params.comment,
    $set: { [`feedback_${params.prompt}_score`]: params.score },
  });
}

/** They closed the card without sending. Score is set if they had
 *  tapped a face before closing. */
export function phFeedbackDismissed(params: { prompt: string; score: number | null }): void {
  if (!POSTHOG_KEY) return;
  posthog.capture('feedback_dismissed', {
    prompt: params.prompt,
    score: params.score,
  });
}

/** Track HIPAA authorization signed (previously Intercom-only). */
export function phHipaaSigned(params: { forFamilyMember?: boolean } = {}): void {
  if (!POSTHOG_KEY) return;
  posthog.capture('hipaa_signed', {
    for_family_member: params.forFamilyMember ?? false,
  });
}

/* -------------------------------------------------- */
/*  Free accounts (added 2026-10-05)
/* -------------------------------------------------- */

/** A free account uploaded a bill. It's parked until they pay. */
export function phFreeBillUploaded(): void {
  if (!POSTHOG_KEY) return;

  // The activation report needs only the action, never document details.
  posthog.capture('free_bill_uploaded', {});
}

/** The free first look finished and the result is on screen.
 *  The extra fields say what the screen actually showed, so we can tell
 *  a summary statement with no findings apart from an itemized bill that
 *  came back clean, and whether the "bills this size usually come down
 *  by" range was there. */
export function phFreeLookViewed(params: {
  status: string;
  findings: number;
  amountInQuestion: number | null;
  itemized?: boolean | null;
  checks?: number | null;
  moreFindings?: number | null;
  amountDue?: number | null;
  estimateLow?: number | null;
  estimateHigh?: number | null;
  msToResult?: number | null;
}): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('free_look_viewed', {
    status: params.status,
    findings: params.findings,
    amount_in_question: params.amountInQuestion,
    itemized: params.itemized ?? null,
    checks: params.checks ?? null,
    more_findings: params.moreFindings ?? null,
    amount_due: params.amountDue ?? null,
    estimate_low: params.estimateLow ?? null,
    estimate_high: params.estimateHigh ?? null,
    estimate_shown: params.estimateLow != null && params.estimateHigh != null,
    ms_to_result: params.msToResult ?? null,
  });
}

/** They scrolled far enough on the free case page to see the offer card
 *  (price and the start button). Fires once per visit. If people leave
 *  before this, the offer is too far down, not too expensive. */
export function phFreeLookOfferSeen(params: { findings: number; estimateShown: boolean }): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('free_look_offer_seen', {
    findings: params.findings,
    estimate_shown: params.estimateShown,
  });
}

/** Something on the free case page that isn't the start button:
 *  opening their own bill, asking to replace the file, the details sheet. */
export function phFreeLookAction(action: string): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('free_look_action', { action });
}

/** The pay popup was shown. `trigger` says what opened it. Plan and price
 *  are the ones selected when it opened, so price tests show up here. */
export function phPaywallViewed(params: {
  trigger: string;
  hasDraft: boolean;
  plan?: string;
  price?: number;
}): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('paywall_viewed', {
    trigger: params.trigger,
    has_draft: params.hasDraft,
    plan: params.plan ?? null,
    price: params.price ?? null,
  });
}

/** Someone on a free account opened chat to ask before paying.
 *  `source` is where they tapped it. */
export function phQuestionOpened(params: { source: string }): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('question_opened', { source: params.source });
}

/** The pay popup was closed without picking a plan. */
export function phPaywallDismissed(params: { trigger: string }): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('paywall_dismissed', { trigger: params.trigger });
}

/* -------------------------------------------------- */
/*  Failure tracking (added 2026-08-08)
/* -------------------------------------------------- */

/**
 * Track a user-facing failure. Before this existed there was no event for a
 * failed bill upload, a failed checkout-creation call or a failed login — so
 * upload success rate, the step that gates the entire product, could not be
 * measured at all. Every one of those paths only did console.error + a toast.
 *
 * `step` is the machine-readable failure point (e.g. 'bill_upload_storage').
 * Keep the message short and non-identifying; it is a diagnostic, not a log.
 */
export function phFailure(params: {
  step: string;
  message?: string;
  code?: string | number;
  context?: Record<string, unknown>;
}): void {
  if (!POSTHOG_KEY) return;

  posthog.capture('action_failed', {
    step: params.step,
    message: params.message ? String(params.message).slice(0, 300) : null,
    code: params.code ?? null,
    ...(params.context ?? {}),
  });
}

/**
 * Upload first, account after (/start, 2026-10-16). One event name per
 * step so the funnel reads straight off: start_viewed, start_files_picked,
 * start_uploaded, start_email_submitted, start_upload_claimed.
 */
export function phStartStep(step: string, props: Record<string, unknown> = {}): void {
  if (!POSTHOG_KEY) return;

  posthog.capture(`start_${step}`, props);
}
